<template>
  <section class="pub-info-bar show-on-load">
    <div
      class="pub-info-bar__container container"
      :class="{
        ...detectComponentVariant(variantedComponents.PUB_INFO_BAR),
      }"
    >
      <div class="pub-info-bar__text-container">
        <div class="pub-info-bar__popover">
          <PopoverModal v-model:shown="isNavigationModalOpen" placement="bottom">
            <template #trigger>
              <button
                class="pub-info-bar__pub-info"
                :class="{
                  'pub-info-bar__pub-info--open': isNavigationModalOpen,
                }"
                :aria-expanded="isNavigationModalOpen"
                :aria-label="`${fields.pubInfo?.value} - click to change location`"
                @keydown.tab.shift.exact="closeNavigationModal"
                @keydown.esc="closeNavigationModal"
              >
                <i class="icon icon-Location" aria-hidden="true"></i>
                <span class="pub-info-bar__pub-info-link">{{ fields.pubInfo?.value }}</span>
              </button>
            </template>
            <div class="pub-info-bar__navigation-modal-content">
              <ul class="pub-info-bar__navigation-modal-content-list">
                <li v-if="fields.homepageLink?.value?.href">
                  <router-link
                    :to="fields.homepageLink.value.href"
                    @click="onNavigationLinkClick(t('change-location-homepage'))"
                  >
                    {{ t('change-location-homepage') }}
                  </router-link>
                </li>
                <li v-if="googleMapsUrl">
                  <a
                    :href="googleMapsUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click="onNavigationLinkClick(t('change-location-get-directions'))"
                  >
                    {{ t('change-location-get-directions') }}
                  </a>
                </li>
                <li v-if="fields.venueFinderLink?.value?.href">
                  <a
                    :href="fields.venueFinderLink.value.href"
                    @click="onNavigationLinkClick(t('change-location-venue-finder'))"
                    @keydown.tab.exact="closeNavigationModal"
                  >
                    {{ t('change-location-venue-finder') }}
                  </a>
                </li>
              </ul>
            </div>
          </PopoverModal>
        </div>
        <div class="pub-info-bar__opening-hours">
          {{ openingHours }}
        </div>
      </div>
      <div v-if="fields.isSpecialHours?.value" class="pub-info-bar__occasion-info">
        <CustomTooltip
          :label="'Toggle ' + t('pub-info-bar-tooltip-link-label')"
          placement="bottom-end"
        >
          <template #default="{ close }">
            <template v-if="fields.occasionName?.value">
              {{ fields.occasionName?.value }} {{ t('pub-info-bar-tooltip') + ' ' }}
            </template>
            <template v-else>
              {{ t('pub-info-bar-tooltip-no-occasion-name') + ' ' }}
            </template>

            <router-link
              v-if="fields.toolTipLink?.value?.href"
              :to="fields.toolTipLink.value.href"
              @keydown.tab.exact="close"
            >
              {{ t('pub-info-bar-tooltip-link-label') }}
            </router-link>
          </template>
        </CustomTooltip>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useDetectVariantByBrandAndTheme,
  variantedComponents,
} from '../../utils/componentVariants';
import PopoverModal from '../2_elements/PopoverModal/PopoverModal.vue';
import CustomTooltip from '../2_elements/CustomTooltip/CustomTooltip.vue';
import { useButtonsAndLinksTracking } from '../../data-layer/hooks/useButtonsAndLinksTracking';
import type { PubInfoBarProps, PubInfoBarFields } from './types';

const detectComponentVariant = useDetectVariantByBrandAndTheme();

const { trackLinkState } = useButtonsAndLinksTracking({ componentName: 'venue_info_bar' });

const { t } = useI18n();

const props = withDefaults(defineProps<PubInfoBarProps>(), {
  fields: () => ({}) as PubInfoBarFields,
  params: () => ({}),
});

const isNavigationModalOpen = ref<boolean>(false);

const googleMapsUrl = computed<string | null>(() => {
  const latitude = props.fields?.coordinates?.value?.['Coordinate.Latitude'];
  const longitude = props.fields?.coordinates?.value?.['Coordinate.Longitude'];
  if (!latitude || !longitude) return null;
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
});

const openingHours = computed<string>(() =>
  props.fields?.openingHours?.value
    ? `${t('pub-info-bar-open')} ${props.fields.openingHours.value}`
    : t('pub-info-bar-closed')
);

const closeNavigationModal = () => {
  isNavigationModalOpen.value = false;
};

const onNavigationLinkClick = (text: string) => {
  trackLinkState(text);
  closeNavigationModal();
};
</script>

<style lang="scss">
@import './scss/PubInfoBar.scss';
</style>
